import { Access, AuthorizationWrapper } from '@features/authorization';
import { msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { MoreVert, Search } from '@mui/icons-material';
import {
  Autocomplete,
  AutocompleteProps,
  Box,
  Button,
  ButtonProps,
  InputAdornment,
  Paper,
  Skeleton,
  TextField,
} from '@mui/material';
import { ComponentProps, Suspense, type PropsWithChildren } from 'react';
import StandardAutocompleteIdsWithFallback, {
  DataAutocompleteProps,
  DataAutocompleteType,
  StandardAutocompleteFallback,
  StandardAutocompleteIds,
} from '../autocomplete/StandardAutocompleteIds';
import StandardMenu, { StandardMenuItem } from '../StandardMenu';

interface Props<
  TOrderBy,
  TDataAutocompleteType extends DataAutocompleteType,
  TDataAutocompleteMultiple extends boolean | undefined,
  TDataAutocompleteDisableClearable extends boolean | undefined,
  TOrganizationsMultiple extends boolean | undefined,
  TOrganizationsDisableClearable extends boolean | undefined,
  TDevicesMultiple extends boolean = false,
  TDevicesDisableClearable extends boolean = false,
  TIrrigationsMultiple extends boolean = false,
  TIrrigationsDisableClearable extends boolean = false,
  TUsersMultiple extends boolean = false,
  TUsersDisableClearable extends boolean = false,
> extends PropsWithChildren {
  dataAutocompletes?: Array<
    DataAutocompleteProps<
      TDataAutocompleteType,
      TDataAutocompleteMultiple,
      TDataAutocompleteDisableClearable
    > & {
      /**
       * Specifies the access level required to render this component.
       * If provided, the component will only render if the user has the corresponding access.
       * If not provided, the component will always render.
       */
      access?: Access;
    }
  >;
  searchFilter?: {
    value: string | null | undefined;
    onChange: (value: string) => void;
    label?: string;
  } | null;
  organizationFilter?: Omit<
    ComponentProps<
      typeof StandardAutocompleteIdsWithFallback<
        'organizations',
        TOrganizationsMultiple,
        TOrganizationsDisableClearable
      >
    > & {
      multiple?: TOrganizationsMultiple;
      access?: Access;
    },
    'dataType'
  > | null;
  irrigationFilter?: Omit<
    ComponentProps<
      typeof StandardAutocompleteIdsWithFallback<
        'irrigations',
        TIrrigationsMultiple,
        TIrrigationsDisableClearable
      >
    >,
    'dataType'
  > | null;
  orderBy?: {
    value: TOrderBy;
    options: { value: TOrderBy; label: string }[];
    onChange: (value: TOrderBy) => void;
  } | null;
  customSelectorFilters?: (
    | {
        type: 'autocomplete';
        access?: Access;
        value: string | null | undefined;
        onChange: (value: string | null | undefined) => void;
        label: string;
        options: { value: string; label: string }[];
        /** @deprecated prefer suspense */
        loading?: boolean;
      }
    | {
        type: 'text';
        access?: Access;
        value: string | null | undefined;
        onChange: (value: string | null | undefined) => void;
        label: string;
      }
  )[];
  actionButtons?:
    | {
        show?: boolean;
        label: string;
        onClick: () => void;
        disabled?: boolean;
        access?: Access;
        ButtonProps?: Partial<ButtonProps>;
      }[]
    | null;
  moreActionsMenu?: {
    hidden?: boolean;
    access?: Access | Access[];
    tooltip?: string;
    items?: StandardMenuItem[];
  } | null;
}

const StandardListViewHeader = <
  TOrderBy,
  TDataAutocompleteType extends DataAutocompleteType,
  TDataAutocompleteMultiple extends boolean = false,
  TDataAutocompleteDisableClearable extends boolean = false,
  TOrganizationsMultiple extends boolean = false,
  TOrganizationsDisableClearable extends boolean = false,
>(
  props: Props<
    TOrderBy,
    TDataAutocompleteType,
    TDataAutocompleteMultiple,
    TDataAutocompleteDisableClearable,
    TOrganizationsMultiple,
    TOrganizationsDisableClearable
  >,
) => {
  const { _ } = useLingui();
  return (
    <Paper
      sx={{
        display: 'flex',
        p: 2,
        gap: 2,
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: 'center',
      }}
    >
      {props.searchFilter != null && (
        <TextField
          placeholder={props.searchFilter.label ?? _(msg`Buscar`)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ opacity: 0.5 }} fontSize="small" />
              </InputAdornment>
            ),
          }}
          value={props.searchFilter.value}
          onChange={(event) => props.searchFilter?.onChange(event.target.value)}
          fullWidth
          sx={{
            flex: 1,
          }}
        />
      )}
      {props.dataAutocompletes?.map(({ access, ...autocompleteProps }, index) => {
        return (
          <Suspense key={index} fallback={<StandardAutocompleteFallback {...autocompleteProps} />}>
            <AuthorizationWrapper access={access} fallback={null}>
              <StandardAutocompleteIds
                {...autocompleteProps}
                sx={{
                  flex: 1,
                  ...autocompleteProps.sx,
                }}
              />
            </AuthorizationWrapper>
          </Suspense>
        );
      })}
      {props.organizationFilter != null && (
        <Suspense
          fallback={
            <StandardAutocompleteFallback
              {...props.organizationFilter}
              dataType="organizations"
              fullWidth
              sx={{
                flex: 1,
                ...props.organizationFilter.sx,
              }}
            />
          }
        >
          <AuthorizationWrapper access={props.organizationFilter.access} fallback={null}>
            <StandardAutocompleteIds
              fullWidth
              dataType="organizations"
              {...props.organizationFilter}
              sx={{
                flex: 1,
                ...props.organizationFilter?.sx,
              }}
            />
          </AuthorizationWrapper>
        </Suspense>
      )}
      {props.irrigationFilter != null && (
        <StandardAutocompleteIds
          fullWidth
          dataType="irrigations"
          {...props.irrigationFilter}
          sx={{
            flex: 1,
            ...props.irrigationFilter?.sx,
          }}
        />
      )}
      {props.orderBy != null && (
        <Autocomplete
          fullWidth
          options={props.orderBy.options.map((option) => option.value)}
          value={props.orderBy.value!}
          getOptionLabel={(option) =>
            props.orderBy?.options.find((o) => o.value === option)?.label ?? ''
          }
          noOptionsText={_(msg`Sin opciones`)}
          onChange={(_, value) => props.orderBy?.onChange(value)}
          renderInput={(params) => <TextField {...params} label={_(msg`Ordenar`)} />}
          disableClearable
          sx={{
            flex: 1,
          }}
        />
      )}
      {props.customSelectorFilters?.map((filter) => {
        if (filter.type === 'autocomplete') {
          if (filter.loading) {
            return (
              <Skeleton
                key={filter.label}
                variant="rounded"
                height={56}
                sx={{
                  flex: 1,
                }}
              />
            );
          } else {
            return (
              <Suspense
                key={filter.label}
                fallback={
                  <Skeleton
                    key={filter.label}
                    variant="rounded"
                    height={56}
                    sx={{
                      flex: 1,
                    }}
                  />
                }
              >
                <AuthorizationWrapper access={filter.access} fallback={null}>
                  <Autocomplete
                    fullWidth
                    options={filter.options.map((option) => option.value)}
                    value={filter.value}
                    getOptionLabel={(option) =>
                      filter.options.find((o) => o.value === option)?.label ?? ''
                    }
                    sx={{
                      flex: 1,
                    }}
                    onChange={(_, value) => filter.onChange(value)}
                    renderInput={(params) => <TextField {...params} label={filter.label} />}
                  />
                </AuthorizationWrapper>
              </Suspense>
            );
          }
        }

        if (filter.type === 'text') {
          return (
            <TextField
              key={filter.label}
              placeholder={filter.label}
              value={filter.value}
              onChange={(event) => filter.onChange(event.target.value)}
              fullWidth
              sx={{
                flex: 1,
              }}
            />
          );
        }
      })}
      {props.children}
      <Box display="flex" alignItems="center" gap={2}>
        {props.actionButtons
          ?.filter((action) => action.show == null || action.show)
          .map((button) => (
            <Suspense
              key={button.label}
              fallback={
                <Skeleton sx={{ placeSelf: 'center' }} variant="rounded">
                  <Button variant="contained" type="button" {...button.ButtonProps}>
                    {button.label}
                  </Button>
                </Skeleton>
              }
            >
              <AuthorizationWrapper access={button.access} fallback={null}>
                <Button
                  sx={{ placeSelf: 'center' }}
                  onClick={button.onClick}
                  disabled={!!button.disabled}
                  variant="contained"
                  type="button"
                  {...button.ButtonProps}
                >
                  {button.label}
                </Button>
              </AuthorizationWrapper>
            </Suspense>
          ))}

        {props.moreActionsMenu &&
          !props.moreActionsMenu.hidden &&
          props.moreActionsMenu.items?.filter((item) => {
            if ('hidden' in item) return !item.hidden;
            return true;
          }).length && (
            <AuthorizationWrapper
              some
              access={props.moreActionsMenu.items.flatMap((item) => {
                if ('hidden' in item && item.hidden) return [];
                if ('access' in item && item.access != null) return item.access;
                return [];
              })}
              fallback={null}
            >
              <StandardMenu
                items={props.moreActionsMenu.items}
                tooltip={props.moreActionsMenu.tooltip}
              >
                <MoreVert />
              </StandardMenu>
            </AuthorizationWrapper>
          )}
      </Box>
    </Paper>
  );
};

export default StandardListViewHeader;
